/**
 * Карточка элемента для доски и календаря.
 *
 * Плотная: тип, название, статус и ответственный. Всё остальное открывается
 * в боковой карточке — на доске важнее охватить взглядом много элементов
 * сразу, чем узнать про один.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDownIcon, ChevronUpIcon, ChevronsUpIcon, FlagIcon } from 'lucide-react';
import type { ItemSummaryDto } from '@project-os/contracts';
import { PRIORITY_LABELS_RU } from '@project-os/contracts';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ItemTypeIcon } from '../item-icon';

/**
 * Приоритет — значок, а не цветная точка.
 *
 * Цветной кружок на карточке означал приоритет, такой же кружок в календаре —
 * ответственного, а на шкале — состояние. Три смысла у одной формы: цвет
 * перестаёт что-либо значить, потому что значит всё сразу.
 *
 * Теперь цвет закреплён за человеком, а срочность читается формой. Стрелки
 * вверх — выше обычного, вниз — ниже; средний приоритет не показывается
 * вовсе: он и есть обычный, и метка на нём — шум.
 */
const PRIORITY_MARK: Partial<Record<string, { icon: typeof ChevronsUpIcon; className: string }>> = {
  CRITICAL: { icon: ChevronsUpIcon, className: 'text-tone-danger' },
  HIGH: { icon: ChevronUpIcon, className: 'text-tone-warning' },
  LOW: { icon: ChevronDownIcon, className: 'text-muted-foreground/60' },
};

/** Значок приоритета. Обычный приоритет метки не получает. */
function PriorityMark({ priority }: { priority: string }) {
  const mark = PRIORITY_MARK[priority];
  if (!mark) return null;

  const Icon = mark.icon;
  const label = `Приоритет: ${PRIORITY_LABELS_RU[priority as keyof typeof PRIORITY_LABELS_RU]}`;
  // Подпись висит на обёртке, а не на svg: у значка нет свойства title, а
  // без подписи форма стрелки останется загадкой для того, кто видит её
  // впервые.
  return (
    <span role="img" aria-label={label} title={label} className="mt-0.5 shrink-0">
      <Icon className={cn('size-3.5', mark.className)} aria-hidden />
    </span>
  );
}

export function ItemCardBody({
  item,
  showStatus = true,
  compact = false,
}: {
  item: ItemSummaryDto;
  showStatus?: boolean;
  compact?: boolean;
}) {
  return (
    <>
      <div className="flex items-start gap-1.5">
        <ItemTypeIcon type={item.type} className="mt-px size-3.5" />
        <span className="min-w-0 flex-1 text-sm leading-snug">{item.title}</span>
        {item.priority ? <PriorityMark priority={item.priority} /> : null}
      </div>

      {compact ? null : (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          {showStatus ? <ItemStatusBadge type={item.type} status={item.status} /> : null}
          {item.isNextAction ? (
            <FlagIcon className="size-3 text-primary" aria-label="Следующее действие" />
          ) : null}
          {item.dueDate ? (
            <span className="text-2xs text-muted-foreground">{formatDate(item.dueDate)}</span>
          ) : null}
          <span className="flex-1" />
          {item.owner ? <UserAvatar user={item.owner} size="sm" title={item.owner.name} /> : null}
        </div>
      )}
    </>
  );
}

/**
 * Перетаскиваемая карточка.
 *
 * Ручка совпадает со всей карточкой, но короткое нажатие остаётся обычным
 * щелчком: перетаскивание начинается только после смещения на несколько
 * пикселей — иначе по элементу нельзя было бы просто кликнуть.
 */
export function DraggableItemCard({
  item,
  columnId,
  onOpen,
  compact,
  showStatus,
  landed,
}: {
  item: ItemSummaryDto;
  /**
   * Колонка (или день), в которой лежит карточка. Карточки тоже являются
   * целями броска, поэтому при наведении на соседнюю карточку dnd-kit
   * возвращает именно её. Без этого признака нельзя понять, в какую
   * колонку на самом деле метит пользователь.
   */
  columnId: string;
  onOpen: (itemId: string) => void;
  compact?: boolean;
  showStatus?: boolean;
  /** Карточка только что переехала — показать её появление на новом месте. */
  landed?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { item, columnId },
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'surface-card surface-card-interactive cursor-grab border border-border/60 p-2.5 select-none',
        'hover:border-border-strong',
        // Оригинал гаснет, но остаётся на месте: убери его совсем — и
        // колонка схлопнется под курсором, а человек потеряет точку отсчёта.
        isDragging && 'drag-source pointer-events-none',
        // Появление на новом месте отвечает на вопрос «куда он делся».
        landed && 'drop-landed',
      )}
      {...attributes}
      {...listeners}
      onClick={() => onOpen(item.id)}
      onKeyDown={(event) => {
        // Enter открывает карточку, остальные клавиши остаются перетаскиванию.
        if (event.key === 'Enter') {
          event.preventDefault();
          onOpen(item.id);
        }
      }}
    >
      <ItemCardBody item={item} compact={compact} showStatus={showStatus} />
    </div>
  );
}

/** Копия карточки, которая летит за курсором. */
export function ItemCardOverlay({ item }: { item: ItemSummaryDto }) {
  return (
    <div className="surface-card drag-ghost w-64 border border-border-strong p-2.5">
      <ItemCardBody item={item} />
    </div>
  );
}
